import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
import Input from "../components/Input";
import Card from "../components/Card";
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon } from "../components/Icons";
import { authAPI } from "../services/api";
import TwoFALoginModal from "../components/TwoFALoginModal";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTwoFAModal, setShowTwoFAModal] = useState(false);
  const [tempUserId, setTempUserId] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await authAPI.login(email, password);

      if (response.success) {
        // Check if 2FA is required
        if (response.requires2FA) {
          setTempUserId(response.tempUserId);
          setShowTwoFAModal(true);
          setLoading(false);
          return;
        }

        // Navigate to dashboard
        navigate("/dashboard");
      }
    } catch (err) {
      if (err.requiresVerification) {
        // Email not verified, redirect to OTP verification
        navigate("/otp-verification", {
          state: {
            email: err.email || email,
            type: "verification",
          },
        });
      } else {
        setError(err.error || "Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = (data) => {
    // Store tokens
    localStorage.setItem("token", data.token);
    localStorage.setItem("refreshToken", data.refreshToken);
    if (data.user) {
      localStorage.setItem("user", JSON.stringify(data.user));
    }

    // Navigate to dashboard
    setShowTwoFAModal(false);
    navigate("/dashboard");
  };

  return (
    <div
      className="min-h-screen bg-[var(--bg-main)] bg-cover bg-center bg-no-repeat flex items-center justify-center px-4 sm:px-6 md:px-8"
      style={{ backgroundImage: "url('/auth-background.png')" }}
    >
      <Card className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-28 h-28 mx-auto mb-4 card-gradient rounded-full flex items-center justify-center">
            <img
              src="/logo-icon.png"
              alt="Aiarbix Logo"
              className="h-20 w-20 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-2">
            Welcome Back
          </h1>
          <p className="text-[var(--text-primary)]">Sign in to your account</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
            <p className="text-red-500 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-6">
          <Input
            type="email"
            label="Email Address"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            icon={<MailIcon className="w-5 h-5" />}
            required
          />

          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon={<LockIcon className="w-5 h-5" />}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-[42px] text-primary hover:text-primary/80 transition-colors"
            >
              {showPassword ? (
                <EyeOffIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>

          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center text-[var(--text-primary)] cursor-pointer">
              <input type="checkbox" className="mr-2 accent-primary" />
              Remember me
            </label>
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="text-[var(--text-primary)] hover:underline"
            >
              Forgot Password?
            </button>
          </div>

          <Button type="submit" fullWidth disabled={loading}>
            {loading ? "Signing In..." : "Sign In"}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-[var(--text-primary)]">
          Don't have an account?{" "}
          <button
            onClick={() => navigate("/register")}
            className="text-[var(--text-primary)] hover:underline font-semibold"
          >
            Sign Up
          </button>
        </div>
      </Card>

      {/* 2FA Modal */}
      <TwoFALoginModal
        show={showTwoFAModal}
        onClose={() => setShowTwoFAModal(false)}
        onVerify={handle2FAVerify}
        tempUserId={tempUserId}
      />
    </div>
  );
};

export default Login;
